/* ──────────────────────────────────────────────────────────────────────────
 * SOP retrieval — the RAG route of §18, and the document half of §13.
 *
 * ── READ THIS BEFORE USING ANY TEXT IN THIS FILE ──
 *
 * These are NOT approved Standard Operating Procedures. Not one of them has
 * been issued by the Nashik district administration, the Maharashtra Police,
 * the NDRF or any other competent authority. They are illustrative
 * paraphrases written for a demonstration, drawn from published crowd-safety
 * literature.
 *
 * That warning is not boilerplate, and it is the reason this module is built
 * the way it is. The single most dangerous thing this platform could do is
 * answer "what is the approved SOP for crowd diversion?" with confident
 * invented procedure. An officer acting on a fabricated SOP in a live crowd
 * is a worse outcome than the officer getting no answer at all.
 *
 * So three rules are enforced in code, not left to the caller:
 *
 *   1. EVERY result carries `approved: false` and the disclaimer travels
 *      with the text. There is no way to retrieve the content without it.
 *   2. Retrieval is DETERMINISTIC KEYWORD MATCHING. No generative model
 *      touches this. A language model asked for an SOP will produce one
 *      whether or not it exists, which is precisely the failure being
 *      designed out.
 *   3. The text is returned VERBATIM with its reference. Nothing is
 *      summarised, blended or rewritten, so an officer can see exactly what
 *      the platform holds and judge it.
 *
 * A real deployment replaces this file with the authority's issued SOP
 * library, and only then may `approved` become true.
 * ────────────────────────────────────────────────────────────────────────── */

export const SOP_DISCLAIMER =
  "ILLUSTRATIVE ONLY — not an approved SOP. Written for demonstration from "
  + "published crowd-safety literature. It has not been issued by any competent "
  + "authority and must not be used to direct field action. Verify against the "
  + "district administration's issued procedure before acting.";

const DOC = (id, title, topic, tags, body, source) => ({
  id, title, topic, tags, body, source,
  approved: false,
  disclaimer: SOP_DISCLAIMER,
});

export const SOP_LIBRARY = [
  DOC(
    "SOP-CD-01",
    { en: "Crowd diversion at a ghat approach", mr: "घाट मार्गावर गर्दी वळवणे" },
    "crowd_diversion",
    ["divert", "diversion", "crowd", "route", "corridor", "ghat", "reroute"],
    [
      "1. Confirm the receiving route has spare capacity before diverting anyone. A diversion into a second constrained corridor converts one problem into two.",
      "2. Divert at a decision point upstream of the congestion, not at its edge. Turning a packed column back on itself is the mechanism behind several recorded crush events.",
      "3. Physically staff the diversion. Signage alone achieves low compliance in a dense, noisy, unfamiliar environment.",
      "4. Announce in the languages the crowd actually speaks, and repeat. Assume a substantial fraction will not hear or will not act.",
      "5. Tell the receiving sector before the flow arrives, not as it arrives.",
      "6. Record the time the diversion opened and closed. Without it the intervention cannot be evaluated afterwards.",
    ].join("\n"),
    "Paraphrased from general pedestrian-flow and mass-gathering safety guidance.",
  ),
  DOC(
    "SOP-CS-01",
    { en: "Crowd density escalation thresholds", mr: "गर्दी घनता स्तर" },
    "density_escalation",
    ["density", "threshold", "escalate", "escalation", "level of service", "los", "band", "critical"],
    [
      "Below 2 persons/m²: normal operation. Free movement, routine monitoring.",
      "2–3 persons/m²: restricted movement. Increase observation, prepare metering at entry.",
      "3–4 persons/m²: begin active inflow control. Individual movement is now constrained by the crowd.",
      "4–5 persons/m²: critical. Halt inflow, open all egress, deploy to the perimeter. Shockwaves can propagate through the crowd at this density.",
      "Above 5 persons/m²: emergency. Involuntary movement; people can no longer control their own position. Treat as a life-safety event regardless of whether anything has yet happened.",
      "Escalation follows the WORST sector, never the average. An average across a theatre conceals exactly the sector that is failing.",
    ].join("\n"),
    "Paraphrased from published level-of-service and crowd-density literature.",
  ),
  DOC(
    "SOP-IE-01",
    { en: "Ingress / egress balance", mr: "प्रवेश-निर्गमन संतुलन" },
    "ingress_egress",
    ["ingress", "egress", "exit", "entry", "evacuation", "balance", "capacity", "clear"],
    [
      "1. The governing question is not how full a sector is. It is whether the rate people are entering is compatible with the rate they could be got out.",
      "2. Compute egress capacity from the exits that are actually open and staffed, not from the plan.",
      "3. A sector where inflow exceeds egress capacity is accumulating a population it cannot disperse, whatever the current occupancy reads.",
      "4. Egress capacity falls as density rises. Do not assume a gate performs at its rated throughput in a packed crowd.",
      "5. Keep a reserve egress route closed and clear rather than open and congested.",
    ].join("\n"),
    "Paraphrased from mass-gathering egress planning guidance.",
  ),
  DOC(
    "SOP-MED-01",
    { en: "Medical response inside a dense crowd", mr: "गर्दीत वैद्यकीय प्रतिसाद" },
    "medical_in_crowd",
    ["medical", "casualty", "ambulance", "responder", "cardiac", "heat", "first aid", "hospital"],
    [
      "1. A vehicle is often slower than a foot team above 3 persons/m². Assess both before committing.",
      "2. Do not push a vehicle into a dense crowd. The attempt itself creates the counter-flow that causes secondary casualties.",
      "3. Clear and hold an extraction lane before moving the casualty, not during.",
      "4. Confirm the receiving facility has a free bay and the required capability before departure. Nearest is not the same as appropriate.",
      "5. In heat conditions expect presentations to rise non-linearly with both temperature and density.",
    ].join("\n"),
    "Paraphrased from mass-gathering medical planning literature.",
  ),
  DOC(
    "SOP-COM-01",
    { en: "Public advisory over PA and VMS", mr: "जनसूचना प्रसारण" },
    "public_advisory",
    ["announcement", "advisory", "pa", "vms", "broadcast", "public", "message", "language"],
    [
      "1. Say what to do, not what is happening. 'Use the east exit' outperforms 'the west exit is congested'.",
      "2. Broadcast in Marathi, Hindi and English at minimum, and in the languages of the pilgrim groups actually present.",
      "3. Expect low compliance. An advisory is the weakest lever available and should not be the only one deployed.",
      "4. Never announce anything that could cause people to turn around inside a dense column.",
      "5. Repeat on a fixed cycle. A single announcement reaches a small fraction of a moving crowd.",
    ].join("\n"),
    "Paraphrased from crowd communication guidance.",
  ),
  DOC(
    "SOP-AUTH-01",
    { en: "Authorisation of operational interventions", mr: "कार्यवाही मंजुरी" },
    "authorisation",
    ["authorise", "authorize", "approve", "approval", "command", "officer", "decision", "who can"],
    [
      "1. No intervention affecting public movement is executed without a named authorising officer.",
      "2. The system may propose, rank and estimate consequences. It may not execute.",
      "3. The authorising officer's identity, the option chosen, the options rejected, and the time of decision are all recorded.",
      "4. An officer may modify a proposed option. The modification is recorded as the decision, not the original proposal.",
      "5. Rejection is a valid and recorded outcome. A decision log containing only approvals is evidence of a broken process.",
    ].join("\n"),
    "Paraphrased from general incident-command doctrine.",
  ),
  DOC(
    "SOP-MP-01",
    { en: "Missing person at a mass gathering", mr: "हरवलेली व्यक्ती" },
    "missing_person",
    ["missing", "lost", "child", "separated", "reunification", "find"],
    [
      "1. Take the report at the nearest help centre and register it immediately; do not wait for a threshold of elapsed time.",
      "2. Most separations resolve within the same sector. Search outward from the point of separation before broadening.",
      "3. For a child, treat as urgent from the first minute and notify the sector police post at once.",
      "4. Use the PA system for reunification only with the reporting adult's consent, and never broadcast a child's identifying details.",
      "5. Record the resolution. An unclosed missing-person report is an open safeguarding matter.",
    ].join("\n"),
    "Paraphrased from mass-gathering safeguarding guidance.",
  ),
];

/* ── Retrieval ─────────────────────────────────────────────────────────
 * Deterministic scoring over tags and title. Not a model. The score is
 * returned so an operator can see how confident the match is and judge a
 * weak one for themselves rather than being handed a confident sentence. */
/* Words that carry no retrieval signal. Without this list a query like
 * "procedure for launching a weather balloon" matches a crowd-diversion
 * document on the word "for" and comes back looking like an answer — the
 * exact weak-match failure this module exists to prevent. */
const STOPWORDS = new Set([
  "the", "and", "for", "what", "which", "who", "how", "are", "our", "was",
  "has", "have", "with", "from", "this", "that", "there", "their", "you",
  "your", "can", "should", "would", "does", "into", "out", "approved",
  "procedure", "sop", "standard", "operating", "kumbh", "sector", "please",
  "show", "tell", "give", "need", "want", "any", "all", "when", "where",
]);

/* A single incidental body hit is not a match. A real match needs a tag,
 * title or topic hit (3 or 2 points each) or several body hits. */
const MIN_SCORE = 3;

export function searchSOP(query, { limit = 3 } = {}) {
  const q = String(query ?? "").toLowerCase().trim();
  if (!q) {
    return {
      query, matches: [], matched: false,
      note: "No query supplied.",
      disclaimer: SOP_DISCLAIMER,
    };
  }

  const terms = q
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 2 && !STOPWORDS.has(t));

  const scored = SOP_LIBRARY.map((doc) => {
    let score = 0;
    terms.forEach((t) => {
      if (doc.tags.some((tag) => tag.includes(t) || t.includes(tag))) score += 3;
      if (doc.title.en.toLowerCase().includes(t)) score += 2;
      if (doc.topic.includes(t)) score += 2;
      if (doc.body.toLowerCase().includes(t)) score += 1;
    });
    return { doc, score };
  })
    .filter((r) => r.score >= MIN_SCORE)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  if (!scored.length) {
    /* The important branch. No match means no match — it does not mean
     * "generate something plausible". */
    return {
      query,
      matches: [],
      matched: false,
      note:
        "No document in the library matches this query. The platform holds a small illustrative set and does not generate procedure it does not have.",
      disclaimer: SOP_DISCLAIMER,
    };
  }

  return {
    query,
    matched: true,
    matches: scored.map(({ doc, score }) => ({
      id: doc.id,
      title: doc.title,
      score,
      /* Verbatim. Nothing summarised, blended or rewritten. */
      body: doc.body,
      source: doc.source,
      approved: doc.approved,
      disclaimer: doc.disclaimer,
    })),
    disclaimer: SOP_DISCLAIMER,
    /* Even the best match is unapproved, and the caller is told so at the
     * top level as well as per document. */
    anyApproved: false,
  };
}
